import React, { useState } from 'react';
import { Box, Typography, Card, Chip, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { playClickSound } from '../../utils/soundEffects';

const COMPETENCY_DATA = [
  {
    subject: "Algorithms & CP",
    status: "CANDIDATE MASTER",
    value: 96,
    angle: 0,
    proof: "Codeforces Candidate Master (Max Rating: 2013), Top 500 Global Rank, 1,000+ Algorithmic Problems Solved across competitive platforms.",
    tags: ["Graph Theory", "Dynamic Programming", "Segment Trees", "Number Theory"],
    color: "#34d399"
  },
  {
    subject: "Systems & C/C++",
    status: "POSIX & C20",
    value: 92,
    angle: 72,
    proof: "Built POSIX Minimal Shell in C (fork/execvp/pipe IPC), Authored C++20 DSA Reference Handbook with memory proofs.",
    tags: ["C / C++20", "POSIX System Calls", "IPC Pipelines", "Memory Architecture"],
    color: "#38bdf8"
  },
  {
    subject: "Full-Stack Web",
    status: "REACT & NODE",
    value: 90,
    angle: 144,
    proof: "Engineered Multi-tenant SaaS Coding Club Platform with RBAC & interactive browser-based Web Terminal Emulator.",
    tags: ["React.js", "TypeScript", "Node.js", "Tailwind / MUI"],
    color: "#c084fc"
  },
  {
    subject: "Databases & Linux",
    status: "POSTGRES & ARCH",
    value: 88,
    angle: 216,
    proof: "PostgreSQL relational schemas, Docker containerized services, CI/CD automated staging, daily Arch Linux workflow.",
    tags: ["PostgreSQL", "Docker", "CI/CD Staging", "Arch / Debian Linux"],
    color: "#fbbf24"
  },
  {
    subject: "Tech Writing",
    status: "LATEX & TIKZ",
    value: 94,
    angle: 288,
    proof: "Authored in-depth LaTeX CS reference handbooks and currently writing long-form biology book manuscript.",
    tags: ["LaTeX Typesetting", "TikZ Memory Diagrams", "Technical Documentation"],
    color: "#f472b6"
  }
];

const CompetencyRadar = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeIdx, setActiveIdx] = useState(0);

  const size = 260;
  const center = size / 2;
  const radius = center - 35;

  const getCoordinates = (angleDeg, valuePercent) => {
    const angleRad = ((angleDeg - 90) * Math.PI) / 180;
    const r = (radius * valuePercent) / 100;
    const x = center + r * Math.cos(angleRad);
    const y = center + r * Math.sin(angleRad);
    return { x, y };
  };

  const points = COMPETENCY_DATA.map((d) => {
    const { x, y } = getCoordinates(d.angle, d.value);
    return `${x},${y}`;
  }).join(' ');

  const activeData = COMPETENCY_DATA[activeIdx];

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3.5, alignItems: 'center' }}>
      {/* SVG Radar Visualization */}
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ overflow: 'visible' }}>
          <defs>
            <radialGradient id="radarRadialGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={isDark ? "#e5a93c" : "#d97706"} stopOpacity="0.45" />
              <stop offset="70%" stopColor={isDark ? "#38bdf8" : "#0284c7"} stopOpacity="0.2" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Background Concentric Rings */}
          {[25, 50, 75, 100].map((ringPercent) => {
            const ringPoints = COMPETENCY_DATA.map((d) => {
              const { x, y } = getCoordinates(d.angle, ringPercent);
              return `${x},${y}`;
            }).join(' ');
            return (
              <polygon
                key={ringPercent}
                points={ringPoints}
                fill="none"
                stroke={isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}
                strokeWidth="1"
                strokeDasharray={ringPercent === 100 ? 'none' : '3 3'}
              />
            );
          })}

          {/* Web Spoke Lines */}
          {COMPETENCY_DATA.map((d) => {
            const { x, y } = getCoordinates(d.angle, 100);
            return (
              <line
                key={d.subject}
                x1={center}
                y1={center}
                x2={x}
                y2={y}
                stroke={isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}
                strokeWidth="1"
              />
            );
          })}

          {/* Value Polygon with Gradient */}
          <polygon
            points={points}
            fill="url(#radarRadialGlow)"
            stroke={isDark ? '#e5a93c' : '#d97706'}
            strokeWidth="2.5"
            style={{ filter: isDark ? 'drop-shadow(0 0 12px rgba(229, 169, 60, 0.4))' : 'none' }}
          />

          {/* Interactive Data Points */}
          {COMPETENCY_DATA.map((d, idx) => {
            const { x, y } = getCoordinates(d.angle, d.value);
            const isSelected = activeIdx === idx;
            return (
              <g
                key={d.subject}
                onClick={() => {
                  playClickSound();
                  setActiveIdx(idx);
                }}
                style={{ cursor: 'pointer' }}
              >
                <circle
                  cx={x}
                  cy={y}
                  r={isSelected ? 7 : 4.5}
                  fill={d.color}
                  stroke={isDark ? '#0d111a' : '#ffffff'}
                  strokeWidth={2}
                  style={{
                    filter: isSelected ? `drop-shadow(0 0 8px ${d.color})` : 'none',
                    transition: 'all 0.2s ease'
                  }}
                />
              </g>
            );
          })}

          {/* Spoke Labels */}
          {COMPETENCY_DATA.map((d, idx) => {
            const { x, y } = getCoordinates(d.angle, 118);
            const isSelected = activeIdx === idx;
            return (
              <text
                key={d.subject}
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fill={isSelected ? (isDark ? '#f8fafc' : '#0f172a') : (isDark ? '#94a3b8' : '#64748b')}
                fontSize="9.5"
                fontFamily='"JetBrains Mono", monospace'
                fontWeight={isSelected ? 800 : 600}
                onClick={() => {
                  playClickSound();
                  setActiveIdx(idx);
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {d.subject}
              </text>
            );
          })}
        </svg>
      </Box>

      {/* Right Column: Selected Competency Breakdown */}
      <Card
        sx={{
          flexGrow: 1,
          p: 2.5,
          borderRadius: '16px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(229, 169, 60, 0.05) 0%, rgba(15, 23, 42, 0.6) 100%)'
            : 'linear-gradient(135deg, rgba(184, 134, 11, 0.05) 0%, rgba(248, 245, 238, 0.8) 100%)',
          border: `1.5px solid ${isDark ? 'rgba(229, 169, 60, 0.3)' : 'rgba(184, 134, 11, 0.25)'}`,
          borderLeft: `5px solid ${activeData.color}`,
          boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.4)' : '0 6px 18px rgba(0,0,0,0.06)',
          width: '100%'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: activeData.color }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', fontSize: '1.05rem' }}>
              {activeData.subject}
            </Typography>
          </Box>
          <Chip
            label={activeData.status}
            size="small"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.72rem',
              fontWeight: 800,
              backgroundColor: `${activeData.color}22`,
              color: activeData.color,
              border: `1px solid ${activeData.color}55`,
              height: '24px'
            }}
          />
        </Box>

        <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: '0.88rem', lineHeight: 1.65, mb: 1.8 }}>
          {activeData.proof}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
          {activeData.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.72rem',
                fontWeight: 700,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                border: `1px solid ${theme.palette.divider}`,
                color: isDark ? '#f1f5f9' : '#334155'
              }}
            />
          ))}
        </Box>
      </Card>
    </Box>
  );
};

export default CompetencyRadar;
